import {
  Controller,
  FetchOptions,
  FetchPageOptions,
  AnalyticsStore,
  PageStore,
  codeSplit
} from "bernie-core";
import { PageData } from "bernie-http";
import {
  ExtendedContextStore,
  FlexBffTemplateResponse,
  Template
} from "typings/flexFramework/FlexDefinitions";
import { TemplateStore } from "stores/TemplateStore";
import { FlexViewModelStore } from "stores/flexViewModel/FlexViewModelStore";
import { BlossomRendererFactory } from "components/flexFramework/renderer/BlossomRendererFactory";
import { BlossomRendererWithTemplate } from "components/flexFramework/renderer/BlossomRendererWithTemplate";

interface FlexFetchPageOptions extends FetchPageOptions {
  context: ExtendedContextStore;
}

interface WindowWithState extends Window {
  __STATE__: {
    templateStore: TemplateStore;
  };
}

/**
 * New controller that currently only serves Hotel-Reviews type pages.
 * Currently work in progress in order to work towards the BEXG Tech Strategy.
 * More info here: More info: https://confluence.expedia.biz/display/~mhaddock/Flex%3A+full+adoption+of+Tech+Strategy
 */
export class FlexController implements Controller {
  public pageId: string; // x-page-id is set in the middleware
  public path = "/*Hotel-Reviews"; // TODO: Need to find a better way to switch between controllers
  public routeName = "flex-FlexPage";
  public bundles = ["flex-FlexPage"];
  public exact = false;
  public component = codeSplit(async () => {
    const routeComponent = import(/* webpackChunkName: "flex-FlexPage" */ "views/FlexPage") as any;
    let template: Template | undefined;

    // Client-side only
    if (typeof window !== "undefined") {
      const serializedState = (window as WindowWithState).__STATE__;
      template =
        serializedState && serializedState.templateStore
          ? serializedState.templateStore.template
          : undefined;
    }

    if (template) {
      await BlossomRendererFactory.get({ template }).codeSplitAllReactComponents();
    } else {
      await new BlossomRendererWithTemplate([]).codeSplitAllReactComponents();
    }

    return routeComponent;
  });

  public fetch(options: FetchOptions): Promise<any> {
    const { stores } = options;
    const flexViewModelStore = stores && stores.get<FlexViewModelStore>("flexViewModel");
    const pageStore = stores && stores.get<PageStore>("page");
    const analyticsStore = stores && stores.get<AnalyticsStore>("analytics");
    const contextStore = stores && stores.get<ExtendedContextStore>("context");
    const templateStore = stores && stores.get<TemplateStore>("templateStore");

    // We get it from the middleware which hijacks the contextStore
    const flexBffResponse = contextStore.viewModelResponse as FlexBffTemplateResponse;
    const footer: string = contextStore.footer;
    delete contextStore.footer;
    delete contextStore.viewModelResponse;

    flexViewModelStore.setupFlexViewModelStore(
      flexBffResponse,
      pageStore,
      contextStore,
      analyticsStore,
      footer
    );

    templateStore.setup(flexBffResponse);

    return Promise.resolve(templateStore.fetchAndStoreBreadcrumbsData(contextStore));
  }

  public fetchPageData(options: FlexFetchPageOptions): Promise<PageData> {
    const bffResponse = options.context.viewModelResponse as FlexBffTemplateResponse;
    const additionalScripts = BlossomRendererFactory.get({
      template: bffResponse.template
    })
      .getWebpackReactComponents()
      .map(component => component && component.webpackChunkName);

    const seoMetaData = import(/* webpackChunkName: "SeoUtils" */ "utils/seo/SeoUtils").then(
      ({ default: _ }) => {
        return (
          _.buildPageDataWithSeoMetaDataFromFlexBffTemplate(bffResponse) || {
            title: bffResponse.template.title
          }
        );
      }
    );

    return Promise.resolve(seoMetaData).then(m => {
      return {
        ...m,
        assetsToIncludeFilter: (assetKey: string) => additionalScripts.includes(assetKey)
      };
    });
  }
}
